import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NfcReaderService } from '../../../services/nfc-reader.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import * as moment from 'moment';

@Component({
  selector: 'app-store-nfc-monitoring',
  templateUrl: './store-nfc-monitoring.component.html',
  styleUrls: ['./store-nfc-monitoring.component.scss']
})
export class StoreNfcMonitoringComponent implements OnInit {
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;
  
  storeId: string = '';
  storeName: string = '';
  statusData: any[] = [];
  summaryStats: any[] = [];
  dateRange: any = {};
  isLoading = false;
  selectedInterval = '15min';
  uptimePercentage: number = 0;
  storeInfo: any = {};
  startDate: string = '';
  endDate: string = '';
  date: any;

  // Chart configuration
  public lineChartData: ChartConfiguration['data'] = {
    datasets: [
      {
        data: [],
        label: 'NFC Reader Status',
        backgroundColor: 'rgba(0,123,255,0.2)',
        borderColor: 'rgba(0,123,255,1)',
        pointBackgroundColor: 'rgba(0,123,255,1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(0,123,255,1)',
        fill: 'origin',
        stepped: true
      }
    ],
    labels: []
  };

  public lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0
      }
    },
    scales: {
      y: {
        min: 0,
        max: 1,
        ticks: {
          stepSize: 1,
          callback: function(value) {
            return value === 0 ? 'Offline' : 'Online';
          }
        },
        title: {
          display: true,
          text: 'Status'
        }
      }
    },
    plugins: {
      legend: { display: true }
    }
  };
  
  public lineChartType: ChartType = 'line';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private nfcReaderService: NfcReaderService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {
    // Set default date range (7 days ago to today)
    this.startDate = moment().subtract(7, 'days').format('YYYY-MM-DD');
    this.endDate = moment().format('YYYY-MM-DD');
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.storeId = params['id'];
      if (this.storeId) {
        this.loadStatusData();
      } else {
        this.toastr.error('Store ID is required', 'Error');
        this.router.navigate(['/stores']);
      }
    });
  }

  clearDate() {
    this.startDate = '';
    this.endDate = '';
    this.date = '';
    this.loadStatusData();
  }

  updateRangeDate(event: any) {
    if (event.start && event.end) {
      this.startDate = moment(event.start).startOf('day').format('YYYY-MM-DD');
      this.endDate = moment(event.end).endOf('day').format('YYYY-MM-DD');
      this.loadStatusData();
    }
  }

  loadStatusData(): void {
    this.isLoading = true;
    this.spinner.show();

    // Convert dates to ISO strings if they exist
    const startDateIso = this.startDate ? moment(this.startDate).toISOString() : undefined;
    const endDateIso = this.endDate ? moment(this.endDate).toISOString() : undefined;
    
    this.nfcReaderService.getStoreStatusData(this.storeId, startDateIso, endDateIso, this.selectedInterval).subscribe({
      next: (response) => {
        if (response.status) {
          this.statusData = response.data.statusData;
          this.summaryStats = response.data.summaryStats;
          this.dateRange = response.data.dateRange;
          this.uptimePercentage = response.data.uptimePercentage;
          this.storeInfo = response.data.storeInfo;
          this.storeName = this.storeInfo?.storeName || 'Store';
          this.updateChartData();
        } else {
          this.toastr.error(response.message, 'Error');
        }
        this.isLoading = false;
        this.spinner.hide();
      },
      error: (error) => {
        console.error('Error fetching NFC reader data:', error);
        this.toastr.error('Failed to load NFC reader data', 'Error');
        this.isLoading = false;
        this.spinner.hide();
      }
    });
  }

  updateChartData(): void {
    // Process data for chart
    const timestamps: string[] = [];
    const statusValues: number[] = [];

    const storeStatusData = this.statusData
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    
    storeStatusData.forEach(item => {
      // Format timestamp as string instead of using Date object
      const date = new Date(item.timestamp);
      const formattedDate = `${date.toISOString().slice(0, 16).replace('T', ' ')}`;
      timestamps.push(formattedDate);
      statusValues.push(item.status === 'online' ? 1 : 0);
    });

    // Update chart data
    this.lineChartData.labels = timestamps;
    if (this.lineChartData.datasets && this.lineChartData.datasets.length > 0) {
      this.lineChartData.datasets[0].data = statusValues;
      // Update color based on status
      this.lineChartData.datasets[0].backgroundColor = statusValues.map(val => 
        val === 1 ? 'rgba(75, 192, 75, 0.2)' : 'rgba(255, 99, 132, 0.2)'
      );
      this.lineChartData.datasets[0].borderColor = statusValues.map(val => 
        val === 1 ? 'rgba(75, 192, 75, 1)' : 'rgba(255, 99, 132, 1)'
      );
    }

    // Update chart
    if (this.chart) {
      this.chart.update();
    }
  }

  onIntervalChange(event: any): void {
    this.selectedInterval = event.target.value;
    this.loadStatusData();
  }

  getOnlineCount(): number {
    const onlineStats = this.summaryStats.find(item => item._id === 'online');
    return onlineStats ? onlineStats.count : 0;
  }

  getOfflineCount(): number {
    const offlineStats = this.summaryStats.find(item => item._id === 'offline');
    return offlineStats ? offlineStats.count : 0;
  }

  getTotalReadingsCount(): number {
    return this.summaryStats.reduce((total, item) => total + item.count, 0);
  }

  getLastStatusTimestamp(status: string): string {
    const statsItem = this.summaryStats.find(item => item._id === status);
    if (statsItem && statsItem.lastTimestamp) {
      return moment(statsItem.lastTimestamp).format('YYYY-MM-DD HH:mm:ss');
    }
    return 'N/A';
  }

  navigateBack(): void {
    const storesPage = +this.route.snapshot.queryParamMap.get('storesPage')! || 1;
    const storesWord = this.route.snapshot.queryParamMap.get('storesWord') || '';
    const fromStoresList =
      this.route.snapshot.queryParamMap.has('storesPage') ||
      !!storesWord;
    if (fromStoresList) {
      this.router.navigate(['/stores'], {
        queryParams: {
          page: storesPage > 1 ? storesPage : null,
          word: storesWord || null,
        },
      });
    } else {
      this.router.navigate(['/nfc-monitoring']);
    }
  }
} 